import { Spinner, Stack, useDisclosure } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  CreateSubscriptionInput,
  CreateSubscriptionMutation,
  SubscriptionInfosFragment,
  SubscriptionResponse,
  UpdateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useGetFundraisingQuery,
  useGetSubscriptionsQuery,
} from '@blockpulse3/graphql/hooks';
import { extractNodesFromEdges } from '@blockpulse3/helpers';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';

import {
  FundraisingSubscribersCSVModal,
  FundraisingSubscribersModal,
} from '../../../../FundraisingSubscribersModal';
import { IFundraisingSubscriber } from '../../../../FundraisingSubscribersModal/types';
import { isLegalSubscriber, parseSubscriber } from '../../../../utils';
import { fundraisingSubscriberDefaultValues } from '../schema';
import { IFundraisingSubscribersTable } from '../types';
import {
  SubscribersTable,
  SubscribersTableControls,
  SubscribersTableEmpty,
} from './SubscribersTable';

type Props = unknown;

export function FundraisingSubscribersPanel(): JSX.Element {
  /* ** Import CSV modal disclosure ** */
  const subscriptionFileModal = useDisclosure();
  /* ** New subscriber modal disclosure ** */
  const subscriptionFormModal = useDisclosure();

  const { fundraisingId = '' } = useParams();

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    skip: !fundraisingId,
  });

  const { data: dataSub, refetch: refetchSub } = useGetSubscriptionsQuery({
    variables: {
      fundraisingId,
      first: 150,
    },
  });

  useEffect(() => {
    refetchSub();
  }, [fundraisingId, refetchSub]);

  const subscriptions = useMemo(() => {
    if (dataSub?.subscriptions) {
      return extractNodesFromEdges(dataSub.subscriptions as SubscriptionResponse);
    }
    return [];
  }, [dataSub?.subscriptions]);

  /* ** Search input to filter subscriber's table ** */
  const [searchInput, setSearchInput] = useState<string>('');
  /* ** Subscriber table key ** */
  const [currentKey, setCurrentKey] = useState<string>('');
  /* ** State holding subscriber form info for the modal ** */
  const [currentSubscriber, setCurrentSubscriber] = useState<IFundraisingSubscriber>(
    fundraisingSubscriberDefaultValues,
  );
  /* ** Track modal editing state ** */
  const [isEditing, setIsEditing] = useState<boolean>(false);
  /* ** Set a company triggering the alert component if registration number is associated to a different company name/mail ** */
  const [invalidLegalSubscriber, setInvalidLegalSubscriber] = useState<
    SubscriptionInfosFragment['identity']['companyIdentity'] | undefined
  >(undefined);

  const fundraising = data?.fundraising;

  const subscribersInformations: IFundraisingSubscribersTable = useMemo(() => {
    return subscriptions
      ? subscriptions.reduce((acc, cur) => ({ ...acc, [cur.id]: parseSubscriber(cur) }), {})
      : {};
  }, [subscriptions]);

  const isSubscribersListEmpty = Object.values(subscribersInformations).length === 0;

  const [deleteSubscription] = useDeleteSubscriptionMutation();

  /* ** Current displayed subscribers ** */
  const filteredSubscribers = useMemo(() => {
    if (searchInput === '') {
      return subscribersInformations;
    }
    const ids = Object.keys(subscribersInformations).filter((id) => {
      const sub = subscribersInformations[id].subscriber;
      if (isLegalSubscriber(sub)) {
        return (
          sub.name.includes(searchInput) ||
          sub.email.includes(searchInput) ||
          sub.identificationNumber.includes(searchInput)
        );
      }
      return (
        sub.firstName.includes(searchInput) ||
        sub.lastName.includes(searchInput) ||
        sub.email.includes(searchInput)
      );
    });
    return ids.reduce((acc, cur) => ({ ...acc, [cur]: subscribersInformations[cur] }), {});
  }, [searchInput, subscribersInformations]);

  /* ** Subscriber table toolbar callbacks ** */
  const handleSearchInputChange = (input: string): void => {
    setSearchInput(input);
  };

  const handleImportSubscriptionOpen = (): void => {
    subscriptionFileModal.onOpen();
  };

  const handleImportSubscriptionClose = (): void => {
    subscriptionFileModal.onClose();
  };

  const handleCreateSubscriptionOpen = (): void => {
    setCurrentSubscriber(fundraisingSubscriberDefaultValues);
    setCurrentKey('');
    setSearchInput('');
    setIsEditing(false);
    subscriptionFormModal.onOpen();
  };

  const handleCreateSubscriptionClose = (): void => {
    setCurrentSubscriber(fundraisingSubscriberDefaultValues);
    subscriptionFormModal.onClose();
  };

  /* ** Subscriber table callbacks ** */
  const handleSubscriberEdit = (key: string, data: IFundraisingSubscriber): void => {
    setCurrentKey(key);
    setCurrentSubscriber(data);
    setIsEditing(true);
    subscriptionFormModal.onOpen();
  };

  const handleSubscriberDelete = (subscriptionId: string): void => {
    deleteSubscription({
      variables: {
        subscriptionId,
      },
      onCompleted: () => {
        refetchSub();
      },
    });
  };

  /* ** Subscriber form modal callbacks ** */
  const handleSubscriberModalComplete = (
    data: CreateSubscriptionMutation | UpdateSubscriptionMutation,
    subscription: CreateSubscriptionInput,
    isLegal: boolean,
  ): void => {
    refetchSub().then(() => {
      if (isLegal) {
        const createdSubscription =
          (data as CreateSubscriptionMutation)?.createSubscription ||
          (data as UpdateSubscriptionMutation)?.updateSubscription;
        if (createdSubscription) {
          const createdCompany = createdSubscription.identity.companyIdentity;
          if (
            createdCompany?.name !== subscription.name ||
            createdCompany?.signer?.individualIdentity?.email !== subscription.email
          ) {
            setInvalidLegalSubscriber(createdCompany);
          }
        }
      }
    });
  };

  if (!fundraising || loading) {
    // TODO: remove spinner
    return <Spinner />;
  }

  if (error) {
    return <ErrorQueryCard />;
  }

  return (
    <Stack spacing="4">
      <SubscribersTableControls
        onCreateSubscription={handleCreateSubscriptionOpen}
        onImportSubscriptions={handleImportSubscriptionOpen}
        onSearchInputChange={handleSearchInputChange}
      />
      {isSubscribersListEmpty ? (
        <SubscribersTableEmpty />
      ) : (
        <SubscribersTable
          invalidLegalSubscriber={invalidLegalSubscriber}
          rows={filteredSubscribers}
          onSubscriberDelete={handleSubscriberDelete}
          onSubscriberEdit={handleSubscriberEdit}
        />
      )}
      <FundraisingSubscribersModal
        defaultValues={currentSubscriber}
        fundraisingId={fundraisingId}
        isEditing={isEditing}
        isOpen={subscriptionFormModal.isOpen}
        setInvalidLegalSubscriber={setInvalidLegalSubscriber}
        subscriptionId={currentKey}
        onClose={handleCreateSubscriptionClose}
        onComplete={handleSubscriberModalComplete}
      />
      <FundraisingSubscribersCSVModal
        fundraisingId={fundraisingId}
        isOpen={subscriptionFileModal.isOpen}
        onClose={handleImportSubscriptionClose}
        onComplete={refetchSub}
      />
    </Stack>
  );
}

export type FundraisingSubscribersPanelProps = Props;
